/**
 * Journal du dossier lisible (vague 4) : libellés français des actions et des champs, partagés par
 * le serveur (construction des entrées, `core/activity.ts`) et l'interface (affichage).
 * L'interface affiche par exemple « Statut : Réceptionné → En cours ».
 */
import type { InterventionStatus } from './schemas/common.js';
import type { ActivityCategory } from './schemas/dossiers.js';

/** Libellés des statuts côté garage (repris par le journal). */
export const INTERVENTION_STATUS_LABELS: Record<InterventionStatus, string> = {
  received: 'Réceptionné',
  in_progress: 'En cours',
  awaiting_decision: 'Attente décision client',
  awaiting_parts: 'Attente pièces',
  ready: 'Prêt',
  closed: 'Restitué',
};

export const DECISION_LABELS: Record<'accepted' | 'refused' | 'postponed' | 'change_requested', string> = {
  accepted: 'Accepté',
  refused: 'Refusé',
  postponed: 'Reporté',
  change_requested: 'Modification demandée',
};

export const DECISION_CHANNEL_LABELS: Record<'portal' | 'phone' | 'in_person', string> = {
  portal: 'sur le suivi en ligne',
  phone: 'par téléphone',
  in_person: 'sur place',
};

/**
 * Actions du journal d'un dossier. Une action absente de cette liste s'affiche avec son code ;
 * un test vérifie que chaque action enregistrée par l'API y figure.
 */
export const ACTIVITY_ACTION_LABELS: Record<string, string> = {
  'intervention.created': 'Entrée du véhicule',
  'intervention.updated': 'Dossier modifié',
  'intervention.ready': 'Véhicule prêt',
  'intervention.ready_cancelled': 'Mise à disposition annulée',
  'intervention.released': 'Sortie du véhicule',
  'intervention.closed': 'Dossier clôturé',
  'intervention.reopened': 'Dossier rouvert',
  'intervention.storage_waived': 'Frais de gardiennage offerts',
  'intervention.release_report_updated': 'Compte rendu complété (préconisations, échéance)',
  'customer.created': 'Client créé',
  'customer.updated': 'Client modifié',
  'vehicle.created': 'Véhicule créé',
  'vehicle.updated': 'Véhicule modifié',
  'work_item.created': 'Travail ajouté',
  'work_item.updated': 'Travail modifié',
  'work_item.deleted': 'Travail supprimé',
  'media.uploaded': 'Photo ajoutée',
  'media.caption_updated': 'Légende de photo modifiée',
  'media.published': 'Photos publiées au client',
  'media.published_with_complement': 'Photos publiées avec les travaux complémentaires',
  'media.unpublished': 'Photo retirée de la vue client',
  'media.deleted': 'Photo supprimée',
  'complement.created': 'Travaux complémentaires préparés',
  'complement.updated': 'Travaux complémentaires modifiés',
  'complement.submitted': 'Travaux complémentaires soumis à validation',
  'complement.sent': 'Travaux complémentaires envoyés au client',
  'complement.cancelled': 'Travaux complémentaires annulés',
  'complement.cancelled_at_release': 'Travaux complémentaires annulés à la sortie',
  'complement.decided': 'Décision du client',
  'complement.decision_recorded': 'Décision du client saisie par le garage',
  'complement.revision_opened': 'Révision des travaux complémentaires ouverte',
  // Vague 6 : fiche de réception et rendez-vous.
  'entry_report.draft_saved': 'Fiche de réception enregistrée (brouillon)',
  'entry_report.signature_set': 'Signature de la fiche de réception',
  'entry_report.validated': 'Fiche de réception validée',
  'entry_report.shared': 'Fiche de réception partagée avec le client',
  'entry_report.acknowledged': 'Prise de connaissance de la fiche de réception',
  'visit.planned': 'Rendez-vous planifié',
  'visit.updated': 'Rendez-vous modifié',
  'visit.cancelled': 'Rendez-vous annulé',
  'visit.checked_in': 'Entrée du véhicule (rendez-vous)',
  // Vague 6 : protection des données (fiche client).
  'customer.photo_consent_recorded': 'Accord du client pour sa photo',
  'customer.photo_consent_withdrawn': 'Accord pour la photo retiré',
  'customer.erased': 'Données du client effacées',
  'customer.exported': 'Données du client exportées',
  // Council n°2 (08/10/2026) : récapitulatif pour le logiciel de facturation.
  'billing_summary.copied': 'Récapitulatif de facturation copié',
  'billing_summary.exported': 'Récapitulatif de facturation exporté',
  'question.asked': 'Question du client',
  'message.answered': 'Réponse du garage',
  'message.handled': 'Message traité',
  'portal.link_created': 'Lien de suivi partagé',
  'portal.report_link_created': 'Lien du compte rendu créé',
  'portal.links_revoked': 'Liens de suivi révoqués',
  'portal.report_link_revoked': 'Lien du compte rendu révoqué',
  'portal.link_emailed': 'Lien de suivi renvoyé par e-mail',
  // Photos de fiche (vague 4).
  'vehicle.photo_set': 'Photo du véhicule changée',
  'vehicle.photo_removed': 'Photo du véhicule retirée',
  'customer.photo_set': 'Photo du client changée',
  'customer.photo_removed': 'Photo du client retirée',
  // E-mails du journal des envois (L4), ajoutés au journal du dossier : jamais leur contenu.
  'email.queued': 'E-mail en attente d’envoi',
  'email.sent': 'E-mail envoyé',
  'email.failed': 'Échec d’envoi d’un e-mail',
  'email.cancelled': 'E-mail annulé',
};

/** Champs pouvant apparaître dans `ActivityEntry.changes` (liste blanche, voir `core/activity.ts`). */
export const ACTIVITY_FIELD_LABELS: Record<string, string> = {
  // Dossier
  status: 'Statut',
  location: 'Emplacement',
  assignee: 'Mécanicien assigné',
  customerRequest: 'Demande du client',
  entryConditionNotes: 'État à l’entrée',
  // Portail centré sur l'intervention en cours (02/10/2026).
  plannedWork: 'Travaux prévus',
  estimatedTotalTtc: 'Montant estimé TTC',
  receivedAt: 'Date d’entrée',
  // Intégration vague 7 : créneau d'agenda du dossier (valeur `agendaSlotValue`).
  agendaSlot: 'Créneau d’agenda',
  readyAt: 'Prêt le',
  freeUntil: 'Gardiennage gratuit jusqu’au',
  releasedAt: 'Sortie le',
  entryMileage: 'Kilométrage d’entrée',
  exitMileage: 'Kilométrage de sortie',
  // Compte rendu de revente (08/10/2026).
  recommendations: 'Préconisations',
  // Véhicule
  plate: 'Plaque',
  plateKind: 'Type de plaque',
  make: 'Marque',
  model: 'Modèle',
  version: 'Version',
  vin: 'VIN',
  owner: 'Propriétaire',
  // Client et véhicule (02/10/2026) : remarque interne de la fiche.
  notes: 'Remarque',
  // Client
  kind: 'Type de client',
  firstName: 'Prénom',
  lastName: 'Nom',
  companyName: 'Raison sociale',
  email: 'E-mail',
  phone: 'Téléphone',
  civility: 'Civilité',
  // Travaux et lignes
  label: 'Libellé',
  reference: 'Référence',
  quantity: 'Quantité',
  unitPriceHt: 'Prix unitaire HT',
  vatRate: 'TVA',
  maintenanceCategory: 'Catégorie',
  lineKind: 'Nature',
  totalHt: 'Total HT',
  totalTtc: 'Total TTC',
  // Photos, compléments
  caption: 'Légende',
  visibility: 'Visibilité',
  title: 'Titre',
  complementVersion: 'Version des travaux complémentaires',
  comment: 'Modification demandée',
  decision: 'Décision',
  channel: 'Canal',
  // Client (vague 6)
  emailOptOut: 'E-mails',
};

/**
 * Créneau d'agenda dans le journal lisible (`changes` du champ `agendaSlot`) : intervalle ISO 8601
 * `<début UTC>/PT<minutes>M` (« 2026-10-02T08:00:00.000Z/PT90M »), une seule valeur texte ; `null` sans
 * créneau. L'interface le lit avec `parseAgendaSlotValue` pour l'afficher dans le fuseau du garage.
 */
export function agendaSlotValue(slot: { start: string; minutes: number }): string {
  return `${slot.start}/PT${slot.minutes}M`;
}

export function parseAgendaSlotValue(value: string): { start: string; minutes: number } | null {
  const m = /^(.+)\/PT(\d+)M$/.exec(value);
  if (!m || Number.isNaN(Date.parse(m[1] as string))) return null;
  return { start: m[1] as string, minutes: Number(m[2]) };
}

/** Champs « montant » du journal : retirés pour un compte sans `amounts.view`. */
export const ACTIVITY_AMOUNT_FIELDS: readonly string[] = ['unitPriceHt', 'totalHt', 'totalTtc', 'estimatedTotalTtc'];

/** Regroupement à l'affichage : modifications successives d'un même auteur à moins de 2 minutes. */
export const ACTIVITY_GROUP_WINDOW_MS = 2 * 60 * 1000;

export const ACTIVITY_CATEGORY_LABELS: Record<ActivityCategory, string> = {
  dossier: 'Dossier et statut',
  vehicle_customer: 'Véhicule et client',
  complements: 'Travaux complémentaires',
  photos: 'Photos',
  messages: 'Messages',
  links_emails: 'Liens et e-mails',
  release_storage: 'Sortie et gardiennage',
};

/** Catégorie de chaque action du journal (filtre). Un test vérifie que toute action connue en a une. */
export const ACTION_CATEGORY: Record<string, ActivityCategory> = {
  'intervention.created': 'dossier',
  'intervention.updated': 'dossier',
  'intervention.ready': 'dossier',
  'intervention.ready_cancelled': 'dossier',
  'intervention.closed': 'release_storage',
  'intervention.released': 'release_storage',
  'intervention.reopened': 'release_storage',
  'intervention.storage_waived': 'release_storage',
  'intervention.release_report_updated': 'release_storage',
  'customer.created': 'vehicle_customer',
  'customer.updated': 'vehicle_customer',
  'vehicle.created': 'vehicle_customer',
  'vehicle.updated': 'vehicle_customer',
  'vehicle.photo_set': 'vehicle_customer',
  'vehicle.photo_removed': 'vehicle_customer',
  'customer.photo_set': 'vehicle_customer',
  'customer.photo_removed': 'vehicle_customer',
  'work_item.created': 'dossier',
  'work_item.updated': 'dossier',
  'work_item.deleted': 'dossier',
  'media.uploaded': 'photos',
  'media.caption_updated': 'photos',
  'media.published': 'photos',
  'media.published_with_complement': 'photos',
  'media.unpublished': 'photos',
  'media.deleted': 'photos',
  'complement.created': 'complements',
  'complement.updated': 'complements',
  'complement.submitted': 'complements',
  'complement.sent': 'complements',
  'complement.cancelled': 'complements',
  'complement.cancelled_at_release': 'complements',
  'complement.decided': 'complements',
  'complement.decision_recorded': 'complements',
  'complement.revision_opened': 'complements',
  'entry_report.draft_saved': 'dossier',
  'entry_report.signature_set': 'dossier',
  'entry_report.validated': 'dossier',
  'entry_report.shared': 'links_emails',
  'entry_report.acknowledged': 'dossier',
  'visit.planned': 'dossier',
  'visit.updated': 'dossier',
  'visit.cancelled': 'dossier',
  'visit.checked_in': 'dossier',
  'customer.photo_consent_recorded': 'vehicle_customer',
  'customer.photo_consent_withdrawn': 'vehicle_customer',
  'customer.erased': 'vehicle_customer',
  'customer.exported': 'vehicle_customer',
  'billing_summary.copied': 'dossier',
  'billing_summary.exported': 'dossier',
  'question.asked': 'messages',
  'message.answered': 'messages',
  'message.handled': 'messages',
  'portal.link_created': 'links_emails',
  'portal.report_link_created': 'links_emails',
  'portal.links_revoked': 'links_emails',
  'portal.report_link_revoked': 'links_emails',
  'portal.link_emailed': 'links_emails',
  'email.queued': 'links_emails',
  'email.sent': 'links_emails',
  'email.failed': 'links_emails',
  'email.cancelled': 'links_emails',
};

/** Modèles d'e-mail (journal des envois) : libellés pour le journal du dossier. */
export const EMAIL_TEMPLATE_LABELS: Record<string, string> = {
  'notifications.intervention_received': 'Accusé de réception du véhicule',
  'notifications.photos_published': 'Nouvelles photos',
  'notifications.complement_sent': 'Travaux complémentaires à valider',
  'notifications.complement_decided': 'Décision du client (garage)',
  'notifications.question_asked': 'Question du client (garage)',
  'notifications.question_answered': 'Réponse du garage',
  'notifications.vehicle_ready': 'Véhicule prêt',
  'notifications.storage_reminder': 'Rappel de gardiennage',
  'notifications.release_report': 'Compte rendu de sortie',
  // Vague 5 : accès du support (e-mails au propriétaire, hors dossier).
  'notifications.support_requested': 'Demande d’accès du support (propriétaire)',
  'notifications.support_ended': 'Fin de l’accès du support (propriétaire)',
  // Vague 6 : fiche de réception et rendez-vous.
  'notifications.entry_report_shared': 'Fiche de réception',
  'notifications.visit_confirmed': 'Rendez-vous confirmé',
  'notifications.visit_reminder': 'Rappel de rendez-vous',
  // Vague 7 : bilan hebdomadaire (propriétaires, hors dossier).
  'notifications.weekly_digest': 'Bilan hebdomadaire (propriétaire)',
  'notifications.maintenance_reminder': 'Rappel de prochaine échéance',
  // 02/10/2026 : lien de suivi renvoyé à la demande du garage.
  'notifications.portal_link_resent': 'Lien de suivi renvoyé',
  // 02/10/2026 : invitation dans l’équipe (journal « E-mails envoyés », hors dossier).
  'team.invitation': 'Invitation dans l’équipe',
};

export const EMAIL_STATUS_LABELS: Record<'queued' | 'sent' | 'failed' | 'cancelled', string> = {
  queued: 'En attente',
  sent: 'Envoyé',
  failed: 'Échec',
  cancelled: 'Annulé',
};
